import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CurrentDashboardProfileContext, DashboardProfileContextService } from '@rma/authentication/public/user';
import { FINANCE } from '@rma/common-ui-domain/types';
import { ProfileType } from '@rma/generic/domain/to-be-sorted';
import { nonNullable } from '@rma/generic/util/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/util/operators/rma-share-replay';
import { cache } from 'decorator-cache-getter';
import { distinctUntilChanged, map, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { NavigationGroup } from '../entities/side-nav-items';
import { IndividualFinanceSideMenuService } from './individual-finance-side-menu.service';
import { IndividualSalesSideMenuService } from './individual-sales-side-menu.service';
import { OfficeSideMenuService } from './office-side-menu.service';
import { TeamSideMenuService } from './team-side-menu.service';

@Injectable({
  providedIn: 'root',
})
export class SideNavigationService {
  private readonly location = inject(Location);
  private readonly profileContextService = inject(DashboardProfileContextService);

  private readonly individualFinanceMenuService = inject(IndividualFinanceSideMenuService);
  private readonly individualSalesRoutesService = inject(IndividualSalesSideMenuService);
  private readonly officeRoutesService = inject(OfficeSideMenuService);
  private readonly teamRoutesService = inject(TeamSideMenuService);

  public currentUrl$: Observable<string>;

  private currentUrlSource = new Subject<string>();

  constructor() {
    //this can probably be replace with RouteUtilityService
    this.currentUrl$ = this.currentUrlSource.asObservable().pipe(startWith(this.location.path() || '/'), distinctUntilChanged());
    this.location.onUrlChange((url) => this.currentUrlSource.next(url.replace('/dashboard', '')));
  }

  @cache
  public get navigation$(): Observable<NavigationGroup[]> {
    return this.profileContextService.profileContext$.pipe(
      nonNullable(),
      switchMap((x) => this.mapNavigation(x)),
      map((n) => n.filter((g) => g.items.length > 0)),
      rmaShareReplay(),
    );
  }

  private mapNavigation(profile: CurrentDashboardProfileContext): Observable<NavigationGroup[]> {
    if (profile.primaryVerticalType === FINANCE) {
      return this.individualFinanceMenuService.getNavigation();
    } else {
      if (profile.profileType === 'Team' || profile.profileType === 'Office') {
        return profile.profileType === ProfileType.Team ? this.teamRoutesService.getNavigation() : this.officeRoutesService.getNavigation();
      }

      if (profile.profileType === 'Individual') {
        return this.individualSalesRoutesService.getNavigation();
      }
    }

    return of([]);
  }
}
